class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.requestId = 0;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    const onChange = FoxTheme.utils.debounce(this.onChange.bind(this), 300);
    this.input.addEventListener('input', () => {
      this.cancelSearch();
      onChange();
    });
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('submit', this.onSubmit.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
      this.normalizeSearchPageURL();
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      this.renderSection(this.setupURL());
    }
  }
  getQuery() {
    return this.input.value.trim();
  }

  getNormalizedQuery(query) {
    const trimmedQuery = query.trim();
    // Preserve explicitly entered phrases and Shopify field/operator syntax.
    if (trimmedQuery.includes(':') || trimmedQuery.includes('"')) return trimmedQuery;

    const normalizedQuery = trimmedQuery
      .replace(/\s+/g, ' ')
      .replace(/(\d),(?=\d)/g, '$1.')
      .replace(/(\d)\s*[-–—]\s*(mm|cm|m)\b/gi, '$1 $2');

    // The shop's compact article numbers use two group digits and six item digits.
    // Keep this a normal search, rather than excluding non-SKU matches.
    return normalizedQuery.replace(/^(\d{2})(\d{6})$/, '$1-$2');
  }

  normalizeSearchPageURL() {
    const url = new URL(window.location.href);
    const query = url.searchParams.get('q');
    if (!query) return;

    const normalizedQuery = this.getNormalizedQuery(query);
    const shouldNormalizeQuery = normalizedQuery !== query;
    const shouldNormalizeType = url.searchParams.get('type') !== 'product';
    const shouldNormalizePrefix = url.searchParams.get('options[prefix]') !== 'last';

    if (!shouldNormalizeQuery && !shouldNormalizeType && !shouldNormalizePrefix) return;

    url.searchParams.set('q', normalizedQuery);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    window.location.replace(`${url.pathname}${url.search}`);
  }

  onSubmit(event) {
    const query = this.getQuery();
    if (query.length === 0) {
      event.preventDefault();
      return;
    }

    this.input.value = this.getNormalizedQuery(query);
  }

  clear(event = null) {
    event && event.preventDefault();
    this.cancelSearch();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(FoxTheme.routes.predictive_search_url, window.location.origin);

    url.searchParams.set('q', this.getNormalizedQuery(this.getQuery()));
    url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));

    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,variants.sku');

    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    this.renderSection(this.setupURL());
  }

  cancelSearch() {
    this.requestId += 1;
    this.abortController?.abort();
    this.removeAttribute('results');
    this.removeAttribute('loading');
    this.removeAttribute('error');
    this.resetButton.classList.remove('btn--loading');
    this.input.setAttribute('aria-expanded', 'false');
    this.querySelector('.search__error')?.classList.add('hidden');
  }

  renderSection(url) {
    this.cancelSearch();
    const requestId = this.requestId;
    const urlString = url.toString();
    if (this.cachedMap.has(urlString)) {
      this.renderSearchResults(this.cachedMap.get(urlString));
      this.setAttribute('results', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      return;
    }
    this.renderSectionFromFetch(url, requestId);
  }

  renderSectionFromFetch(url, requestId) {
    const urlString = url.toString();
    this.abortController = new AbortController();
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(`${url.pathname}${url.search}`, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Search request failed');
        return response.text();
      })
      .then((responseText) => {
        if (requestId !== this.requestId) return;
        this.renderSearchResults(responseText);
        this.cachedMap.set(urlString, responseText);
        this.setAttribute('results', 'true');
        this.input.setAttribute('aria-expanded', 'true');
      })
      .catch((error) => {
        if (requestId !== this.requestId || error.name === 'AbortError') return;
        this.setAttribute('error', 'true');
        this.querySelector('.search__error')?.classList.remove('hidden');
      })
      .finally(() => {
        if (requestId !== this.requestId) return;
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        throw new Error('Search response is missing its results section');
      }
    } else {
      throw new Error('Search results container is missing');
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);
